/**
 * Customer service section + standalone PDF version.
 * The email section is embedded inside generateNewOrderEmailHtml,
 * so it must remain a self-contained HTML fragment with no external state.
 */

/**
 * Generate Customer Service section HTML for email
 * @param {string} baseUrl - Base URL for legal links
 * @returns {string} HTML content for customer service section
 */
export function generateCustomerServiceHtml(baseUrl = '') {
  // SVG background decoration for cards
  const cardBgDecoration = `<svg style="position: absolute; top: 0; left: 0; width: 100%; height: 100%; opacity: 0.03; pointer-events: none;" viewBox="0 0 200 200" fill="none"><circle cx="180" cy="20" r="80" fill="#283548"/><circle cx="20" cy="180" r="60" fill="#283548"/></svg>`

  return `
    <!-- Customer Service Section -->
    <div style="margin-top: 32px; border-top: 2px solid #e2e8f0; padding-top: 24px;">
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom: 20px;">
        <tr>
          <td style="text-align: center;">
            <h3 style="margin: 0 0 8px; color: #1e293b; font-size: 18px; font-weight: 700;">מידע ושירות לקוחות</h3>
            <p style="margin: 0; color: #64748b; font-size: 13px;">אנחנו כאן בשבילך</p>
          </td>
        </tr>
      </table>

      <!-- Two Column Layout for Contact Info -->
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="margin-bottom: 20px;">
        <tr>
          <!-- Customer Service -->
          <td style="width: 50%; vertical-align: top; padding: 8px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%); border-radius: 12px; border: 1px solid #e2e8f0; position: relative; overflow: hidden;">
              <tr>
                <td style="padding: 20px; text-align: center; height: 220px; vertical-align: top;">
                  ${cardBgDecoration}
                  <!-- Logo -->
                  <img src="https://firebasestorage.googleapis.com/v0/b/gofood-il.appspot.com/o/logos%2Flogo_300.png?alt=media&token=21d7d83a-7562-462e-b340-24265c9fa915" alt="GoFood" style="width: 60px; height: 60px; object-fit: contain; margin-bottom: 12px; border-radius: 8px;" />
                  <!-- Title -->
                  <div style="font-size: 15px; font-weight: 600; color: #1e293b; margin-bottom: 10px;">שירות לקוחות</div>
                  <!-- Phone -->
                  <a href="tel:0549368597" style="font-size: 22px; font-weight: 700; color: #283548; text-decoration: none; direction: ltr; display: inline-block; margin-bottom: 10px;">054-936-8597</a>
                  <!-- Hours -->
                  <div style="color: #64748b; font-size: 12px; height: 36px;">
                    <div>
                      <span style="display: inline-block; vertical-align: middle;">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      </span>
                      <span style="vertical-align: middle; margin-right: 4px;">ימים א'-ה' | 9:00-17:00</span>
                    </div>
                  </div>
                </td>
              </tr>
            </table>
          </td>

          <!-- Technical Support -->
          <td style="width: 50%; vertical-align: top; padding: 8px;">
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background: linear-gradient(135deg, #f8fafc 0%, #eef2ff 100%); border-radius: 12px; border: 1px solid #e2e8f0; position: relative; overflow: hidden;">
              <tr>
                <td style="padding: 20px; text-align: center; height: 220px; vertical-align: top;">
                  ${cardBgDecoration}
                  <!-- Logo -->
                  <img src="https://firebasestorage.googleapis.com/v0/b/gofood-il.appspot.com/o/logos%2Flogo_300.png?alt=media&token=21d7d83a-7562-462e-b340-24265c9fa915" alt="GoFood" style="width: 60px; height: 60px; object-fit: contain; margin-bottom: 12px; border-radius: 8px;" />
                  <!-- Title -->
                  <div style="font-size: 15px; font-weight: 600; color: #1e293b; margin-bottom: 10px;">תמיכה טכנית</div>
                  <!-- Phone -->
                  <a href="tel:0544221254" style="font-size: 22px; font-weight: 700; color: #283548; text-decoration: none; direction: ltr; display: inline-block; margin-bottom: 10px;">054-422-1254</a>
                  <!-- Hours -->
                  <div style="color: #64748b; font-size: 12px; height: 36px;">
                    <div style="margin-bottom: 2px;">
                      <span style="display: inline-block; vertical-align: middle;">
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
                      </span>
                      <span style="vertical-align: middle; margin-right: 4px;">ימים א'-ה' | 9:00-18:00</span>
                    </div>
                    <div style="font-size: 10px; color: #94a3b8;">סופ"ש ולילות: מענה בהתאם לזמינות</div>
                  </div>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>

      <!-- Legal Links -->
      <table role="presentation" width="100%" cellspacing="0" cellpadding="0" style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 12px; border: 1px solid #e2e8f0;">
        <tr>
          <td style="padding: 20px; text-align: center;">
            <!-- Title -->
            <table role="presentation" cellspacing="0" cellpadding="0" style="margin: 0 auto 16px;">
              <tr>
                <td style="vertical-align: middle;">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#283548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/></svg>
                </td>
                <td style="vertical-align: middle; padding-right: 8px;">
                  <span style="font-size: 14px; font-weight: 600; color: #1e293b;">מסמכים משפטיים</span>
                </td>
              </tr>
            </table>
            <!-- Links -->
            <table role="presentation" width="100%" cellspacing="0" cellpadding="0">
              <tr>
                <td style="width: 33%; text-align: center; padding: 10px;">
                  <a href="${baseUrl}/privacy" style="color: #283548; text-decoration: none; font-size: 13px; font-weight: 500; display: block;">
                    <span style="display: block; margin-bottom: 4px;">📄</span>
                    מדיניות פרטיות
                  </a>
                </td>
                <td style="width: 33%; text-align: center; padding: 10px; border-right: 1px solid #e2e8f0; border-left: 1px solid #e2e8f0;">
                  <a href="${baseUrl}/accessibility" style="color: #283548; text-decoration: none; font-size: 13px; font-weight: 500; display: block;">
                    <span style="display: block; margin-bottom: 4px;">♿</span>
                    הצהרת נגישות
                  </a>
                </td>
                <td style="width: 33%; text-align: center; padding: 10px;">
                  <a href="${baseUrl}/policy" style="color: #283548; text-decoration: none; font-size: 13px; font-weight: 500; display: block;">
                    <span style="display: block; margin-bottom: 4px;">📋</span>
                    תקנון האתר
                  </a>
                </td>
              </tr>
            </table>
          </td>
        </tr>
      </table>
    </div>
  `
}

/**
 * Generate Customer Service PDF HTML
 * @param {string} baseUrl - Base URL for legal links
 * @returns {string} Complete HTML for PDF
 */
export function generateCustomerServicePdfHtml(baseUrl = '') {
  return `
<!DOCTYPE html>
<html lang="he" dir="rtl">
<head>
  <meta charset="UTF-8">
  <meta name="viewport" content="width=device-width, initial-scale=1.0">
  <title>מידע ושירות לקוחות - GoFood</title>
  <style>
    @import url('https://fonts.googleapis.com/css2?family=Rubik:wght@400;500;600;700&display=swap');
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: 'Rubik', Arial, sans-serif;
      direction: rtl;
      background: #fff;
      color: #1e293b;
      padding: 30px;
      font-size: 14px;
      line-height: 1.6;
    }
  </style>
</head>
<body>
  <div style="max-width: 100%; margin: 0 auto;">
    <!-- Header -->
    <div style="text-align: center; border-bottom: 3px solid #283548; padding-bottom: 20px; margin-bottom: 30px;">
      <h1 style="font-size: 28px; font-weight: 700; color: #1e293b; margin-bottom: 4px;">GoFood</h1>
      <h2 style="font-size: 20px; font-weight: 600; color: #283548; margin-bottom: 8px;">מידע ושירות לקוחות</h2>
      <p style="font-size: 13px; color: #64748b;">אנחנו כאן בשבילך בכל שאלה או בקשה</p>
    </div>

    <!-- Customer Service Box -->
    <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 16px; padding: 24px; border: 2px solid #e2e8f0; margin-bottom: 20px;">
      <div style="display: flex; align-items: center; margin-bottom: 16px;">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#283548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/></svg>
        <h3 style="margin-right: 12px; font-size: 18px; font-weight: 700; color: #1e293b;">שירות לקוחות לאחר ביצוע הזמנה</h3>
      </div>
      <div style="padding-right: 44px;">
        <div style="font-size: 28px; font-weight: 700; color: #283548; margin-bottom: 12px; direction: ltr; text-align: right;">054-936-8597</div>
        <div style="display: flex; align-items: center; color: #475569; font-size: 14px;">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
          <span style="margin-right: 8px;"><strong>שעות פעילות:</strong> ימים א' עד ה' | 9:00-17:00</span>
        </div>
      </div>
    </div>

    <!-- Technical Support Box -->
    <div style="background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%); border-radius: 16px; padding: 24px; border: 2px solid #e2e8f0; margin-bottom: 20px;">
      <div style="display: flex; align-items: center; margin-bottom: 16px;">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#283548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z"/></svg>
        <h3 style="margin-right: 12px; font-size: 18px; font-weight: 700; color: #1e293b;">תמיכה טכנית באתר ובאפליקציה</h3>
      </div>
      <div style="padding-right: 44px;">
        <div style="font-size: 28px; font-weight: 700; color: #283548; margin-bottom: 12px; direction: ltr; text-align: right;">054-422-1254</div>
        <div style="color: #475569; font-size: 14px;">
          <div style="display: flex; align-items: center; margin-bottom: 8px;">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#64748b" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><circle cx="12" cy="12" r="10"/><polyline points="12 6 12 12 16 14"/></svg>
            <span style="margin-right: 8px;"><strong>שעות פעילות:</strong> ימים א' עד ה' | 9:00-18:00</span>
          </div>
          <div style="background: #e0e7ff; border-radius: 8px; padding: 10px 14px; color: #4338ca; font-size: 13px; margin-top: 8px;">
            <strong>סופ"ש ושעות הלילה:</strong> יינתן מענה בהתאם לזמינות הכוננים
          </div>
        </div>
      </div>
    </div>

    <!-- Legal Documents Box -->
    <div style="background: #f8fafc; border-radius: 16px; padding: 24px; border: 2px solid #e2e8f0;">
      <div style="display: flex; align-items: center; margin-bottom: 16px;">
        <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#283548" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/><polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/><line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/></svg>
        <h3 style="margin-right: 12px; font-size: 18px; font-weight: 700; color: #1e293b;">מסמכים משפטיים</h3>
      </div>
      <div style="padding-right: 40px;">
        <table width="100%" style="border-collapse: collapse;">
          <tr>
            <td style="padding: 12px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="color: #283548; font-weight: 600; font-size: 15px;">📄 מדיניות פרטיות</span>
              <div style="color: #64748b; font-size: 12px; margin-top: 4px;">${baseUrl}/privacy</div>
            </td>
          </tr>
          <tr>
            <td style="padding: 12px 0; border-bottom: 1px solid #e2e8f0;">
              <span style="color: #283548; font-weight: 600; font-size: 15px;">♿ הצהרת נגישות</span>
              <div style="color: #64748b; font-size: 12px; margin-top: 4px;">${baseUrl}/accessibility</div>
            </td>
          </tr>
          <tr>
            <td style="padding: 12px 0;">
              <span style="color: #283548; font-weight: 600; font-size: 15px;">📋 תקנון האתר</span>
              <div style="color: #64748b; font-size: 12px; margin-top: 4px;">${baseUrl}/policy</div>
            </td>
          </tr>
        </table>
      </div>
    </div>

    <!-- Footer -->
    <div style="text-align: center; margin-top: 30px; padding-top: 20px; border-top: 1px solid #e2e8f0;">
      <p style="color: #64748b; font-size: 12px;">תודה שבחרת ב-GoFood!</p>
      <p style="color: #94a3b8; font-size: 11px; margin-top: 4px;">© ${new Date().getFullYear()} GoFood. כל הזכויות שמורות.</p>
    </div>
  </div>
</body>
</html>
  `.trim()
}
